import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'

export default function CostManagement() {
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Fetch dashboard cost analytics
  const { data: costAnalytics, isLoading: analyticsLoading } = useQuery(
    'cost-analytics',
    () => api.get('/api/jobs/analytics/costs')
  )

  // Fetch detailed cost analytics
  const { data: detailedAnalytics, isLoading: detailedLoading } = useQuery(
    'detailed-cost-analytics',
    () => api.get('/api/jobs/analytics/costs/detailed')
  )

  // Fetch period-based analytics
  const { data: periodAnalytics, isLoading: periodLoading } = useQuery(
    ['period-cost-analytics', selectedPeriod, startDate, endDate],
    () => {
      const params = new URLSearchParams({ period: selectedPeriod })
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      return api.get(`/api/jobs/analytics/costs/by-period?${params}`)
    },
    {
      enabled: !!selectedPeriod,
    }
  )

  if (analyticsLoading || detailedLoading) {
    return <LoadingSpinner />
  }

  const analytics = costAnalytics?.data
  const detailed = detailedAnalytics?.data
  const periodData = periodAnalytics?.data

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Cost Management
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Track and analyze API costs across all jobs and time periods
          </p>
        </div>
      </div>

      {/* Dashboard Integration - Existing Cost Stats */}
      <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
        <div className='card'>
          <div className='card-body'>
            <div className='flex items-center'>
              <div className='flex-shrink-0'>
                <CurrencyDollarIcon className='h-8 w-8 text-green-600' />
              </div>
              <div className='ml-5 w-0 flex-1'>
                <dl>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                    Total API Costs
                  </dt>
                  <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                    ${analytics?.total_costs?.toFixed(2) || '0.00'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='card-body'>
            <div className='flex items-center'>
              <div className='flex-shrink-0'>
                <ChartBarIcon className='h-8 w-8 text-blue-600' />
              </div>
              <div className='ml-5 w-0 flex-1'>
                <dl>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                    Average Cost per Job
                  </dt>
                  <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                    ${analytics?.avg_cost_per_job?.toFixed(2) || '0.00'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='card-body'>
            <div className='flex items-center'>
              <div className='flex-shrink-0'>
                <ClockIcon className='h-8 w-8 text-purple-600' />
              </div>
              <div className='ml-5 w-0 flex-1'>
                <dl>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                    Total API Calls
                  </dt>
                  <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                    {analytics?.total_api_calls || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='card-body'>
            <div className='flex items-center'>
              <div className='flex-shrink-0'>
                <CalendarIcon className='h-8 w-8 text-orange-600' />
              </div>
              <div className='ml-5 w-0 flex-1'>
                <dl>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                    Jobs with Costs
                  </dt>
                  <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                    {analytics?.jobs_with_costs || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Period Analytics */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Time Period Analytics
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Analyze costs by different time periods
          </p>
        </div>
        <div className='card-body'>
          <div className='mb-6'>
            <div className='flex items-center space-x-4'>
              <div>
                <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Period:
                </label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className='input text-sm ml-2'
                >
                  <option value='day'>Day</option>
                  <option value='week'>Week</option>
                  <option value='month'>Month</option>
                  <option value='year'>Year</option>
                </select>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Start Date:
                </label>
                <input
                  type='date'
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className='input text-sm ml-2'
                />
              </div>
              <div>
                <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                  End Date:
                </label>
                <input
                  type='date'
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className='input text-sm ml-2'
                />
              </div>
            </div>
          </div>

          {periodLoading ? (
            <LoadingSpinner />
          ) : periodData ? (
            <div className='space-y-4'>
              {/* Summary Stats */}
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
                <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                  <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    Total Cost
                  </div>
                  <div className='text-2xl font-bold text-gray-900 dark:text-white'>
                    ${periodData.summary?.total_cost?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                  <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    Jobs Run
                  </div>
                  <div className='text-2xl font-bold text-gray-900 dark:text-white'>
                    {periodData.summary?.total_jobs || 0}
                  </div>
                </div>
                <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                  <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    Avg Cost/Job
                  </div>
                  <div className='text-2xl font-bold text-gray-900 dark:text-white'>
                    $
                    {periodData.summary?.avg_cost_per_job?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>

              {/* Cost Breakdown Table */}
              <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                <table className='min-w-full divide-y divide-gray-300 dark:divide-gray-600'>
                  <thead className='bg-gray-50 dark:bg-gray-700'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        {selectedPeriod === 'day'
                          ? 'Date'
                          : selectedPeriod === 'week'
                          ? 'Week'
                          : selectedPeriod === 'month'
                          ? 'Month'
                          : 'Year'}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        Jobs Run
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        Total Cost
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        Text Search Cost
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        Place Details Cost
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        Avg Cost/Job
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600'>
                    {Object.entries(periodData.period_data || {}).map(
                      ([period, data]: [string, any]) => (
                        <tr key={period}>
                          <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white'>
                            {period}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300'>
                            {data.job_count}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300'>
                            ${data.total_cost?.toFixed(2) || '0.00'}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300'>
                            ${data.text_search_cost?.toFixed(2) || '0.00'}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300'>
                            ${data.place_details_cost?.toFixed(2) || '0.00'}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300'>
                            ${data.avg_cost?.toFixed(2) || '0.00'}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
              <ChartBarIcon className='h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600' />
              <p>No cost data available for the selected period</p>
            </div>
          )}
        </div>
      </div>

      {/* State Breakdown */}
      {detailed?.state_breakdown &&
        Object.keys(detailed.state_breakdown).length > 0 && (
          <div className='card'>
            <div className='card-header'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                Cost Breakdown by State
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                Total costs and job counts by state
              </p>
            </div>
            <div className='card-body'>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {Object.entries(detailed.state_breakdown).map(
                  ([state, data]: [string, any]) => (
                    <div
                      key={state}
                      className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'
                    >
                      <div className='flex items-center justify-between'>
                        <div>
                          <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                            {state}
                          </div>
                          <div className='text-lg font-bold text-gray-900 dark:text-white'>
                            ${data.total_cost?.toFixed(2) || '0.00'}
                          </div>
                          <div className='text-xs text-gray-500 dark:text-gray-400'>
                            {data.job_count} jobs
                          </div>
                        </div>
                        <div className='text-right'>
                          <div className='text-sm text-gray-500 dark:text-gray-400'>
                            Avg: ${data.avg_cost?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  )
}
